import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { EmptyStateComponent } from '../../../../components/empty-state/empty-state.component';
import { SkeletonComponent } from '../../../../components/skeleton/skeleton.component';
import {
  ITemplateModalResult,
  TemplateModalComponent,
} from '../../../../components/template-modal/template-modal.component';
import { RowLinkDirective } from '../../../../directives/row-link.directive';
import { TemplateModel } from '../../../../models/template.model';
import { ConfirmService } from '../../../../services/confirm.service';
import {
  ITemplateCreateRequest,
  ITemplateUpdateRequest,
  TemplateService,
} from '../../../../services/template.service';
import { injectIsMobile } from '../../../../utils/breakpoint.util';

/** Opciones del filtro de estado. El API no pagina ni filtra este listado. */
type StatusFilter = 'all' | 'active' | 'inactive';

/**
 * Como va una plantilla respecto al servidor. Sin entrada en `rowStates` = en
 * reposo (sincronizada). Mismo criterio que `template-page` (detalle) para
 * sus campos, pero MAS SIMPLE: aqui una alta que falla NO se queda "en error"
 * esperando reintento, se revierte al instante (ver `persistCreate`), asi que
 * no hace falta guardar el body para reintentar ni un estado `'error'`.
 *
 * `'deactivating'` es el equivalente para el borrado SOFT (`deactivate()`):
 * tampoco se queda "en error" esperando reintento, la fila solo vuelve a su
 * estado previo si el DELETE falla (ver `persistDeactivate`).
 */
type TemplateSyncStatus = 'creating' | 'updating' | 'deactivating';

interface ITemplateRowState {
  status: TemplateSyncStatus;
}

/** Prefijo de los ids temporales de una alta optimista. Ver `createTempId`. */
const TEMP_ID_PREFIX = 'tmp-';

/**
 * Listado de plantillas: tabla en escritorio, tarjetas en móvil.
 *
 * Una plantilla define la FORMA de una credencial (que campos tiene), nunca su
 * contenido: esta consola no toca la boveda.
 *
 * ALTA, EDICION Y DESACTIVAR: patron OPTIMISTA, no bloqueante — el mismo que
 * ya resuelve `template-page` (detalle) para sus campos. El modal cierra de
 * inmediato (o, en desactivar, la confirmacion se resuelve) y la fila
 * aparece/cambia en la tabla al instante, marcada "Guardando…" o
 * "Desactivando…"; el POST/PUT/DELETE real corre en segundo plano, SIN el
 * overlay bloqueante global (`createTemplate`/`updateTemplate`/
 * `deleteTemplate` reciben `showSpinner = false`, y la subida de imagen que
 * pueda seguir tambien: `uploadFile(..., false)`). El dueño fue explicito:
 * NINGUNA mutacion de esta pantalla puede bloquearla, sin excepciones — se
 * debe poder seguir editando otras filas mientras una operacion esta en
 * curso. Ver `ITemplateRowState` para el modelo de estados.
 *
 * Tres diferencias deliberadas frente al patron de campos:
 * 1) ALTA EN DOS PASOS (`persistCreate`): primero el POST, y solo si hay
 *    archivo Y `presignFile`, la subida a S3. Si el POST falla la plantilla
 *    NUNCA existio: se revierte quitando la fila (sin estado de error que
 *    reintentar). Si el POST sale bien pero falla la subida, la plantilla YA
 *    EXISTE en el servidor: se conserva la fila reconciliada y solo se avisa
 *    del fallo de imagen (lo hace `uploadFile` con su propio toast). Mismo
 *    criterio en `persistUpdate`.
 * 2) ORDEN: la lista viene alfabetica por nombre DESDE EL BACKEND; eso no se
 *    toca (nunca se ordena aqui la respuesta del GET). La fila optimista de
 *    un alta se coloca en su posicion alfabetica solo como colocacion visual
 *    TEMPORAL mientras esta subiendo (`workingTemplates`/`insertAlphabetically`).
 * 3) NACEN DESHABILITADAS: el POST no admite `status`, el API siempre crea
 *    la plantilla con `status = false`. Si el filtro esta en "Solo activas",
 *    la fila recien creada quedaria excluida justo al reconciliar y
 *    desaparecería ante los ojos de quien la acaba de crear; se fuerza
 *    visible (`forceVisibleTemplateIds`) hasta que cambie el filtro o
 *    recargue la lista.
 */
@Component({
  selector: 'app-templates-page',
  imports: [
    EmptyStateComponent,
    FormsModule,
    NgSelectComponent,
    RouterLink,
    RowLinkDirective,
    SkeletonComponent,
  ],
  templateUrl: './templates-page.component.html',
  styleUrl: './templates-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplatesPageComponent implements OnInit {
  private readonly templateService = inject(TemplateService);
  private readonly modalService = inject(NgbModal);
  private readonly confirmService = inject(ConfirmService);

  /**
   * `templateService.loading` es del SERVICIO (singleton): se enciende con
   * CUALQUIER GET, incluido un "Reintentar" manual con la tabla ya poblada
   * (crear, editar y desactivar ya NO disparan un GET: son optimistas y se
   * reconcilian localmente, ver `commitCreatedTemplate`/`replaceTemplateLocally`).
   * NO se usa solo para decidir el esqueleto (ver `showSkeleton`); de lo
   * contrario esa recarga manual volveria a tapar la tabla entera con
   * barras grises, aunque ya hubiera datos en pantalla.
   */
  protected readonly loading = this.templateService.loading;

  /**
   * Por debajo de `MOBILE_BREAKPOINT_QUERY` el listado se pinta como tarjetas
   * en vez de tabla (renderizado condicional, no CSS que oculte un marcado
   * duplicado: evita contenido repetido para lectores de pantalla y buscador
   * del navegador, y no paga el coste de montar dos veces la lista). Misma
   * función de inyección que `DashboardLayoutComponent`, `field-types-page` y
   * `users-page` — ver `src/app/utils/breakpoint.util.ts`.
   */
  protected readonly isMobile = injectIsMobile();

  /**
   * `loaded` arranca en `false` y solo pasa a `true` cuando la peticion TERMINA
   * (con exito o con error). `loading` no basta: arranca en `false`, asi que el
   * primer render pintaria el estado vacio antes de saber si hay plantillas.
   */
  protected readonly loaded = this.templateService.templatesLoaded;
  protected readonly error = this.templateService.templatesError;

  /**
   * El esqueleto es solo para la PRIMERA carga (`!loaded()`) o para una
   * recarga deliberada ("Reintentar" tras un fallo) que arranca SIN datos
   * que enseñar todavia. Cualquier otra recarga en segundo plano con la
   * tabla ya poblada NO vuelve a tapar la pantalla: se queda operable
   * mientras `templateService.loading` esta en `true` por debajo.
   *
   * Depende de `workingTemplates` (no de `templateService.templates` a
   * secas) para que tampoco parpadee mientras hay una alta optimista
   * pendiente (`pendingCreates`) que ya se esta pintando.
   */
  protected readonly showSkeleton = computed(
    () => !this.loaded() || (this.loading() && this.workingTemplates().length === 0),
  );

  /**
   * La tarjeta de error de carga solo se pinta cuando NO hay nada que
   * enseñar en su lugar: si un "Reintentar" manual falla pero la tabla ya
   * tenia datos, esos datos siguen siendo validos y se quedan en pantalla —
   * el fallo ya se anuncio con un toast (`TemplateService.getTemplates` lo
   * lanza en su `catch`).
   */
  protected readonly showLoadError = computed(
    () => !!this.error() && this.workingTemplates().length === 0,
  );

  /** Mismo número de bloques que el esqueleto de filas en escritorio (6). */
  protected readonly skeletonCards = [0, 1, 2, 3, 4, 5];

  protected readonly statusFilter = signal<StatusFilter>('all');
  protected readonly search = signal('');

  /**
   * Opciones fijas del filtro de estado. "Todos los estados" es un item REAL
   * de la lista (no un placeholder): el ng-select se comporta identico al
   * `<select>` nativo que reemplaza.
   */
  protected readonly statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'active', label: 'Solo activas' },
    { value: 'inactive', label: 'Solo inactivas' },
  ];

  /**
   * Estado de sincronizacion por plantilla, indexado por `templateId` (el
   * temporal mientras esta en alta, el real en cuanto se conoce). Sin entrada
   * para un id = esa fila esta en reposo.
   */
  protected readonly rowStates = signal<Record<string, ITemplateRowState>>({});

  /** Filas de alta optimista aun sin resolver. Se insertan solo para pintar. */
  protected readonly pendingCreates = signal<TemplateModel[]>([]);

  /**
   * Ids de plantillas recien creadas que deben verse SIEMPRE, aunque el
   * filtro de estado activo las excluiria (nacen con `status = false`, ver
   * el punto 3 del comentario de la clase). Se vacia al cambiar de filtro
   * (`onStatusFilterChange`) o al recargar la lista (`load`): a partir de ahi
   * el filtro vuelve a mandar, como con cualquier otra plantilla inactiva.
   */
  protected readonly forceVisibleTemplateIds = signal<Set<string>>(new Set());

  /**
   * `templateService.templates()` (la fuente de verdad, orden alfabetico del
   * backend) con las altas optimistas AUN pendientes intercaladas en su
   * posicion alfabetica — SOLO para pintar mientras suben; no se reordena la
   * lista real ni se llama `.sort()` sobre lo que devuelve el API (ver punto
   * 2 del comentario de la clase).
   */
  protected readonly workingTemplates = computed(() => {
    let merged = this.templateService.templates();
    for (const pending of this.pendingCreates()) {
      merged = this.insertAlphabetically(merged, pending);
    }
    return merged;
  });

  /** El listado llega completo: buscar y filtrar se hace en cliente. */
  protected readonly visibleTemplates = computed(() => {
    const term = this.search().trim().toLowerCase();
    const filter = this.statusFilter();
    const forced = this.forceVisibleTemplateIds();

    return this.workingTemplates().filter((template) => {
      const matchesStatus =
        filter === 'all' ||
        (filter === 'active' ? template.status : !template.status) ||
        forced.has(template.templateId);

      const matchesTerm =
        !term ||
        template.name.toLowerCase().includes(term) ||
        template.code.toLowerCase().includes(term) ||
        template.description.toLowerCase().includes(term);

      return matchesStatus && matchesTerm;
    });
  });

  protected readonly activeCount = computed(
    () => this.workingTemplates().filter((template) => template.status).length,
  );

  constructor() {
    // Ninguna escritura sobre signals LOCALES de este componente despues de
    // destruido: una alta/edicion puede seguir en vuelo si el usuario navega
    // fuera a media subida. Los signals COMPARTIDOS del servicio
    // (`templateService.templates`) SI se siguen actualizando aunque este
    // componente ya no exista — son datos reales que otras pantallas leen
    // tambien (`prefills-page`, el KPI del dashboard), a diferencia de
    // `template().fields` en el detalle, que solo le importa a esa ficha.
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
    });
  }

  private destroyed = false;

  ngOnInit(): void {
    void this.load();
  }

  protected async load(): Promise<void> {
    // Recargar es una de las dos formas de "soltar" el forzado de
    // visibilidad de una alta reciente (la otra es cambiar de filtro): a
    // partir de aqui el filtro vuelve a mandar sobre datos frescos.
    this.forceVisibleTemplateIds.set(new Set());
    await this.templateService.getTemplates();
  }

  /**
   * `null` llega cuando se limpia el ng-select con el boton de "x"; se
   * resuelve exactamente igual que elegir "Todos los estados".
   */
  protected onStatusFilterChange(value: StatusFilter | null): void {
    this.statusFilter.set(value ?? 'all');
    this.forceVisibleTemplateIds.set(new Set());
  }

  protected onSearchChange(value: string): void {
    this.search.set(value);
  }

  protected fieldCount(template: TemplateModel): number {
    return template.fields.filter((field) => field.status).length;
  }

  /**
   * `label` del campo ACTIVO marcado como principal de esta plantilla, o
   * `null` si no tiene ninguno. Maximo un campo principal por plantilla (lo
   * garantiza el API), asi que `find` basta — ver el comentario de
   * `primaryField` en `template-field.model.ts`. Mismo criterio que
   * `fieldCount`: helper de fila, no logica en el template.
   */
  protected primaryFieldLabel(template: TemplateModel): string | null {
    const field = template.fields.find((item) => item.primaryField && item.status);
    return field ? field.label : null;
  }

  // ---------------------------------------------------------------------------
  // Estado de fila — helpers de plantilla
  // ---------------------------------------------------------------------------

  protected rowState(template: TemplateModel): ITemplateRowState | undefined {
    return this.rowStates()[template.templateId];
  }

  protected isRowBlocked(template: TemplateModel): boolean {
    return this.rowState(template) !== undefined;
  }

  protected rowStatusText(status: TemplateSyncStatus): string {
    switch (status) {
      case 'creating':
        return 'Guardando…';
      case 'deactivating':
        return 'Desactivando…';
      default:
        return 'Guardando cambios…';
    }
  }

  protected rowActionLabel(base: string, template: TemplateModel): string {
    return this.isRowBlocked(template) ? `${base} (procesando, espera)` : base;
  }

  private setRowState(templateId: string, state: ITemplateRowState): void {
    if (this.destroyed) return;
    this.rowStates.update((states) => ({ ...states, [templateId]: state }));
  }

  private clearRowState(templateId: string): void {
    if (this.destroyed) return;
    this.rowStates.update((states) => {
      if (!(templateId in states)) return states;
      const next = { ...states };
      delete next[templateId];
      return next;
    });
  }

  private createTempId(): string {
    return `${TEMP_ID_PREFIX}${crypto.randomUUID()}`;
  }

  /** Inserta `item` justo antes de la primera plantilla que le gana alfabeticamente. */
  private insertAlphabetically(list: TemplateModel[], item: TemplateModel): TemplateModel[] {
    const index = list.findIndex((existing) => existing.name.localeCompare(item.name) > 0);
    const next = [...list];
    if (index === -1) next.push(item);
    else next.splice(index, 0, item);
    return next;
  }

  /** Clon superficial que conserva el prototipo: evita reconstruir `image`/`fields`. */
  private cloneTemplate(template: TemplateModel, overrides: Partial<TemplateModel>): TemplateModel {
    return Object.assign(
      Object.create(Object.getPrototypeOf(template) as object),
      template,
      overrides,
    ) as TemplateModel;
  }

  /** Reemplaza la plantilla `templateId` por `template`, en su mismo lugar del signal compartido. */
  private replaceTemplateLocally(templateId: string, template: TemplateModel): void {
    this.templateService.templates.update((templates) =>
      templates.map((item) => (item.templateId === templateId ? template : item)),
    );
  }

  // ---------------------------------------------------------------------------
  // Alta — optimista
  // ---------------------------------------------------------------------------

  protected async create(): Promise<void> {
    const result = await this.openModal(null);
    if (!result) return;

    const request: ITemplateCreateRequest = {
      code: result.code,
      name: result.name,
      description: result.description,
      generateQr: result.generateQr,
      contentType: result.contentType,
    };

    this.insertOptimisticTemplate(request, result.file);
  }

  /** Inserta la fila optimista y lanza el POST en segundo plano. No bloquea. */
  private insertOptimisticTemplate(request: ITemplateCreateRequest, file: File | null): void {
    const tempId = this.createTempId();
    const nowIso = new Date().toISOString();

    // `status: false` siempre: el POST no admite ese campo y el API crea
    // toda plantilla nueva inactiva (ver punto 3 del comentario de la
    // clase). Reflejarlo desde este instante evita que la fila "salte" de
    // activa a inactiva cuando llegue la respuesta real.
    const optimisticTemplate = new TemplateModel({
      templateId: tempId,
      code: request.code,
      name: request.name,
      description: request.description,
      fileId: null,
      generateQr: request.generateQr,
      status: false,
      uidCreatedBy: null,
      uidUpdatedBy: null,
      createdAt: nowIso,
      updatedAt: nowIso,
      image: null,
      fields: [],
    });

    this.pendingCreates.update((items) => [...items, optimisticTemplate]);
    this.setRowState(tempId, { status: 'creating' });

    void this.persistCreate(tempId, request, file);
  }

  private async persistCreate(
    tempId: string,
    request: ITemplateCreateRequest,
    file: File | null,
  ): Promise<void> {
    // `showSpinner = false`: el alta ya es optimista, la fila "Guardando…"
    // ya se ve en la tabla. El overlay bloqueante global aqui congelaria la
    // pantalla exactamente igual que el `await` que se elimino del click.
    const created = await this.templateService.createTemplate(request, false);

    // La fila optimista SIEMPRE sale de `pendingCreates`, salga bien o mal:
    // si fallo el POST la plantilla nunca existio en el servidor — a
    // diferencia del alta de campos, aqui NO se deja un estado de error para
    // reintentar, se revierte quitando la fila sin mas (el toast de error ya
    // lo puso `createTemplate`). Si salio bien, el registro real ya vive en
    // `templateService.templates` (mas abajo) y no necesita seguir aqui.
    if (!this.destroyed) {
      this.pendingCreates.update((items) => items.filter((item) => item.templateId !== tempId));
    }

    if (!created) {
      this.clearRowState(tempId);
      return;
    }

    this.commitCreatedTemplate(created.template);

    if (file && created.presignFile) {
      // La plantilla YA EXISTE en el servidor en este punto: si la subida
      // falla NO se revierte nada de lo anterior, solo se avisa del fallo de
      // la imagen (lo hace `uploadFile` con su propio toast). La fila vivia
      // bloqueada bajo el id TEMPORAL (`tempId`); `commitCreatedTemplate` ya
      // la inserto en la lista compartida con su id REAL, asi que el estado
      // "Guardando…" se traslada a ese id para que la fila se quede
      // bloqueada hasta que la imagen termine de subir — sin overlay
      // bloqueante global (`showSpinner = false`): el usuario debe poder
      // seguir operando otras filas mientras tanto.
      this.clearRowState(tempId);
      this.setRowState(created.template.templateId, { status: 'creating' });
      await this.templateService.uploadFile(created.presignFile, file, false);
      this.clearRowState(created.template.templateId);
    } else {
      this.clearRowState(tempId);
    }
  }

  /**
   * Inserta la plantilla YA CREADA en el signal COMPARTIDO del servicio, en
   * su posicion alfabetica. A diferencia de `template().fields` en el
   * detalle (que solo le importa a esa ficha), este listado tambien lo leen
   * `prefills-page` y el KPI del dashboard: el dato real debe reflejarse ahi
   * sin esperar a un GET completo.
   */
  private commitCreatedTemplate(template: TemplateModel): void {
    this.templateService.templates.update((templates) =>
      this.insertAlphabetically(templates, template),
    );

    if (!template.status && !this.destroyed) {
      this.forceVisibleTemplateIds.update((ids) => new Set(ids).add(template.templateId));
    }
  }

  // ---------------------------------------------------------------------------
  // Edicion — optimista
  // ---------------------------------------------------------------------------

  protected async edit(template: TemplateModel): Promise<void> {
    if (this.isRowBlocked(template)) return;

    const result = await this.openModal(template);
    if (!result) return;

    const request: ITemplateUpdateRequest = {
      name: result.name,
      description: result.description,
      generateQr: result.generateQr,
      status: result.status,
      contentType: result.contentType,
    };

    this.applyOptimisticUpdate(template, request, result.file);
  }

  /** Aplica el cambio al instante (marcado "Guardando cambios…") y lanza el PUT detras. */
  private applyOptimisticUpdate(
    previous: TemplateModel,
    request: ITemplateUpdateRequest,
    file: File | null,
  ): void {
    const optimistic = this.cloneTemplate(previous, {
      name: request.name,
      description: request.description,
      generateQr: request.generateQr,
      status: request.status,
      updatedAt: new Date(),
    });

    this.replaceTemplateLocally(previous.templateId, optimistic);
    this.setRowState(previous.templateId, { status: 'updating' });

    void this.persistUpdate(previous, request, file);
  }

  private async persistUpdate(
    previous: TemplateModel,
    request: ITemplateUpdateRequest,
    file: File | null,
  ): Promise<void> {
    // `showSpinner = false`, mismo motivo que en `persistCreate`.
    const updated = await this.templateService.updateTemplate(previous.templateId, request, false);

    if (!updated) {
      // Revierte al valor anterior: la plantilla YA EXISTIA, su valor previo
      // sigue siendo valido. Mismo criterio que la edicion de campos en el
      // detalle: nunca se queda "en error" esperando un reintento.
      this.clearRowState(previous.templateId);
      this.replaceTemplateLocally(previous.templateId, previous);
      return;
    }

    this.replaceTemplateLocally(previous.templateId, updated.template);

    if (file && updated.presignFile) {
      // Los datos YA quedaron guardados en el PUT anterior: si esto falla NO
      // se revierte nada, solo se avisa del fallo de la imagen (lo hace
      // `uploadFile` con su propio toast). La fila se queda en "Guardando
      // cambios…" hasta que la subida termine, con o sin exito — sin
      // overlay bloqueante global (`showSpinner = false`).
      await this.templateService.uploadFile(updated.presignFile, file, false);
    }

    this.clearRowState(previous.templateId);
  }

  // ---------------------------------------------------------------------------
  // Desactivar — optimista (soft delete)
  // ---------------------------------------------------------------------------

  /**
   * Borrado SOFT: la plantilla se desactiva, no desaparece. Mismo patron
   * OPTIMISTA que `create()`/`edit()`: la confirmacion previa (`askDeactivate`)
   * SI se espera —preguntar antes esta bien—, pero en cuanto se confirma la
   * fila pasa al instante a "Desactivando…" y el DELETE corre en segundo
   * plano, SIN overlay bloqueante global (`deleteTemplate(..., false)`). El
   * dueño fue explicito: ninguna mutacion de esta pantalla puede bloquearla,
   * "actualizar estatus" incluido — se debe poder seguir editando otras
   * filas mientras esta se desactiva.
   */
  protected async deactivate(template: TemplateModel): Promise<void> {
    if (this.isRowBlocked(template)) return;

    const confirmed = await this.confirmService.askDeactivate(
      'la plantilla',
      `${template.name} · ${template.code}`,
    );

    if (!confirmed) return;

    this.setRowState(template.templateId, { status: 'deactivating' });
    void this.persistDeactivate(template);
  }

  private async persistDeactivate(template: TemplateModel): Promise<void> {
    // `showSpinner = false`, mismo motivo que `persistCreate`/`persistUpdate`.
    const deactivated = await this.templateService.deleteTemplate(template.templateId, false);

    this.clearRowState(template.templateId);

    if (!deactivated) {
      // La plantilla NUNCA cambio de valor en el cliente (solo se marco la
      // fila como "Desactivando…"): al fallar el DELETE no hay nada que
      // revertir mas alla de quitar ese marcado, y el toast de error ya lo
      // puso `deleteTemplate`.
      return;
    }

    // Reconciliacion LOCAL con lo que confirma el servidor
    // (`status = false`, y cualquier otro campo que el DELETE devuelva):
    // no hace falta un GET completo de la lista para reflejarlo, a
    // diferencia del patron previo a esta correccion.
    this.replaceTemplateLocally(template.templateId, deactivated);
  }

  private async openModal(template: TemplateModel | null): Promise<ITemplateModalResult | null> {
    const reference = this.modalService.open(TemplateModalComponent, {
      centered: true,
      backdrop: 'static',
      scrollable: true,
      ariaLabelledBy: 'ks-template-modal-title',
    });

    (reference.componentInstance as TemplateModalComponent).init(template);

    try {
      return (await reference.result) as ITemplateModalResult;
    } catch {
      return null;
    }
  }
}
